import { Ionicons } from '@expo/vector-icons';
import * as AppleAuthentication from 'expo-apple-authentication';
import {
	exchangeCodeAsync,
	useAuthRequest,
	useAutoDiscovery,
} from 'expo-auth-session';
import { router, useLocalSearchParams } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import React, { useState, useEffect } from 'react';
import {
	Text,
	View,
	StyleSheet,
	TouchableOpacity,
	ActivityIndicator,
	Platform,
} from 'react-native';

import { useSession } from '@/components/auth';
import { AuthDebugButton } from '@/components/auth-debug-button';
import { useVersion } from '@/hooks/use-version';
import { API_BASE_URL } from '@/services/api-service';
import { AZURE_CLIENT_ID, AZURE_REDIRECT_URI } from '@/services/auth-service';
import { authService } from '@/services/auth-service';
import { fetchWithTimeout } from '@/services/http-service';

// Required for Auth
WebBrowser.maybeCompleteAuthSession();

// Azure AD configuration
const AZURE_TENANT_ID = 'common'; // Use 'common' for multi-tenant or your specific tenant ID

const SignIn: React.FC = () => {
	const { signIn, session } = useSession();
	const { version, buildNumber } = useVersion();
	const [azureLoading, setAzureLoading] = useState(false);
	const [isMetaPressed, setIsMetaPressed] = useState(false);
	const [appleLoading, setAppleLoading] = useState(false);
	const [appleAvailable, setAppleAvailable] = useState(false);
	const [demoLoading, setDemoLoading] = useState(false);
	const [apiStatus, setApiStatus] = useState<'checking' | 'healthy' | 'unhealthy' | 'error'>('checking');
	const [apiUrl, setApiUrl] = useState<string>('');

	// Get URL parameters to detect OAuth redirects
	const searchParams = useLocalSearchParams();

	// Also parse URL directly as fallback (useLocalSearchParams might not catch query params on redirect)
	const [urlParams, setUrlParams] = useState<{ code?: string; state?: string; error?: string }>({});

	useEffect(() => {
		if (Platform.OS === 'web' && typeof window !== 'undefined') {
			const url = new URL(window.location.href);
			const params: { code?: string; state?: string; error?: string } = {};
			if (url.searchParams.get('code')) params.code = url.searchParams.get('code') || undefined;
			if (url.searchParams.get('state')) params.state = url.searchParams.get('state') || undefined;
			if (url.searchParams.get('error')) params.error = url.searchParams.get('error') || undefined;
			setUrlParams(params);
		}
	}, []);

	// Use URL params if searchParams don't have them (fallback for redirect scenarios)
	const code = (searchParams.code as string | undefined) || urlParams.code;
	const state = (searchParams.state as string | undefined) || urlParams.state;

	// Redirect authenticated users away from the auth page
	useEffect(() => {
		if (session) {
			router.replace('/');
		}
	}, [session]);

	// Set up Azure Auth
	const azureDiscovery = useAutoDiscovery(
		`https://login.microsoftonline.com/${AZURE_TENANT_ID}/v2.0`,
	);

	// Use the consistent redirect URI from auth service configuration
	const redirectUri = AZURE_REDIRECT_URI;

	const [azureRequest, azureResponse, promptAzureAsync] = useAuthRequest(
		{
			clientId: AZURE_CLIENT_ID,
			scopes: ['openid', 'profile', 'email', 'User.Read', 'offline_access'],
			redirectUri,
		},
		azureDiscovery,
	);



	// Track if we've already processed this code to prevent duplicate processing
	const [processedCode, setProcessedCode] = useState<string | null>(null);

	// Manually process OAuth redirect if useAuthRequest doesn't catch it
	useEffect(() => {
		// Only process if we have code/state in URL but useAuthRequest hasn't processed it yet
		// AND we haven't already processed this specific code
		if (
			Platform.OS === 'web'
			&& code
			&& state
			&& azureDiscovery
			&& (!azureResponse || azureResponse.type === undefined)
			&& processedCode !== code
		) {
			// Get stored state and code verifier from sessionStorage (if we redirected manually)
			const storedState = typeof window !== 'undefined' && window.sessionStorage
				? sessionStorage.getItem('oauth_state')
				: null;
			const storedCodeVerifier = typeof window !== 'undefined' && window.sessionStorage
				? sessionStorage.getItem('oauth_code_verifier')
				: null;

			// If we have stored state from sessionStorage (from manual redirect), use that for validation
			// Otherwise, try to match against azureRequest state (for popup flows)
			// IMPORTANT: When using manual redirect, azureRequest will be recreated with a new state
			// so we MUST prioritize storedState over azureRequest.state
			const expectedState = storedState || azureRequest?.state;

			// Only validate state if we have an expected value
			// If we have storedState, that's the source of truth (we stored it before redirect)
			// If we don't have storedState but have azureRequest.state, validate against that
			// BUT: If we don't have storedState AND azureRequest has a different state (new request),
			// we should still proceed if we have a stored code verifier (from a previous redirect)
			if (expectedState && expectedState !== state) {
				// If we have storedState but it doesn't match, that's a real error
				if (storedState) {
					console.error('State mismatch in OAuth redirect - security issue, aborting');
					return;
				}
				// If no storedState, azureRequest might be a new request, so continue anyway
				// BUT only if we have a stored code verifier (from a previous redirect attempt)
				if (!storedCodeVerifier) {
					return;
				}
			}

			// Wait for azureRequest to be ready if we don't have stored code verifier
			if (!storedCodeVerifier && !azureRequest) {
				return;
			}

			// Mark this code as being processed to prevent duplicate attempts
			setProcessedCode(code);

			// Manually exchange the code for tokens
			const exchangeCode = async () => {
				try {
					setAzureLoading(true);

					const codeVerifier = storedCodeVerifier || azureRequest?.codeVerifier;
					if (!codeVerifier) {
						console.error('No code verifier available for PKCE');
						setAzureLoading(false);
						setProcessedCode(null);
						return;
					}

					const result = await exchangeCodeAsync(
						{
							clientId: AZURE_CLIENT_ID,
							code,
							extraParams: { code_verifier: codeVerifier },
							redirectUri,
						},
						azureDiscovery,
					);

					// Clear stored OAuth data
					if (typeof window !== 'undefined' && window.sessionStorage) {
						sessionStorage.removeItem('oauth_state');
						sessionStorage.removeItem('oauth_code_verifier');
					}

					// Clear the URL parameters after successful auth BEFORE signing in
					// This prevents the useEffect from running again
					if (typeof window !== 'undefined') {
						window.history.replaceState({}, '', '/auth');
					}

					// Sign in with the token (this will redirect to /)
					signIn(result, '/');
				} catch (error) {
					console.error('Error exchanging code for token manually:', error);
					setAzureLoading(false);
					setProcessedCode(null);
				}
			};

			exchangeCode();
		}
	}, [code, state, azureDiscovery, azureRequest, azureResponse, redirectUri, signIn, processedCode]);


	// Check Apple Authentication availability
	useEffect(() => {
		const checkAppleAvailability = async () => {
			if (Platform.OS === 'ios') {
				try {
					const isAvailable = await AppleAuthentication.isAvailableAsync();
					setAppleAvailable(isAvailable);
				} catch {
					setAppleAvailable(false);
				}
			}
		};

		checkAppleAvailability();
	}, []);

	// Check API status
	useEffect(() => {
		const checkApiStatus = async () => {
			try {
				// Get API URL from environment
				const baseUrl = API_BASE_URL;
				setApiUrl(baseUrl);

				// Check status endpoint
				const response = await fetchWithTimeout(`${baseUrl}/status`);
				if (response.ok) {
					const data = await response.json();
					setApiStatus(data.status === 'healthy' ? 'healthy' : 'unhealthy');
				} else {
					setApiStatus('unhealthy');
				}
			} catch {
				setApiStatus('error');
			}
		};

		checkApiStatus();
	}, []);

	// Add event listeners for Meta key press (Cmd on macOS, Ctrl on Windows/Linux)
	useEffect(() => {
		if (Platform.OS !== 'web') return; // Only run on web

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.metaKey || event.ctrlKey) {
				setIsMetaPressed(true);
			}
		};

		const handleKeyUp = (event: KeyboardEvent) => {
			if (!event.metaKey && !event.ctrlKey) {
				setIsMetaPressed(false);
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		document.addEventListener('keyup', handleKeyUp);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.removeEventListener('keyup', handleKeyUp);
		};
	}, []);

	useEffect(() => {
		const processAzureAuth = async () => {
			if (azureRequest && azureResponse?.type === 'success' && azureDiscovery) {
				try {
					const result = await exchangeCodeAsync(
						{
							clientId: AZURE_CLIENT_ID,
							code: azureResponse.params.code,
							extraParams: azureRequest.codeVerifier
								? { code_verifier: azureRequest.codeVerifier }
								: undefined,
							redirectUri,
						},
						azureDiscovery,
					);

					signIn(result, '/');
				} catch (error) {
					console.error('Error exchanging code for token:', error);
				} finally {
					setAzureLoading(false);
				}
			} else if (azureResponse?.type === 'error') {
				console.error('Azure auth error:', azureResponse.error);
				setAzureLoading(false);
			} else if (azureResponse?.type === 'cancel') {
				setAzureLoading(false);
			}
		};

		processAzureAuth();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [azureResponse]);

	const handleAzureSignIn = async () => {
		if (!azureDiscovery) {
			console.error('Cannot sign in: Azure discovery not ready');
			return;
		}
		if (!azureRequest) {
			console.error('Cannot sign in: Azure request not ready');
			return;
		}

		setAzureLoading(true);
		try {
			// Detect if we're in an embedded browser (like Cursor's browser)
			// Embedded browsers often have COOP restrictions that block popups
			const isEmbeddedBrowser = Platform.OS === 'web' && typeof window !== 'undefined' && (
				window.parent !== window // In an iframe
				|| window.opener !== null // Opened as a popup
				|| (typeof navigator !== 'undefined' && navigator.userAgent.includes('Electron')) // Electron-based apps
				|| (typeof document !== 'undefined' && document.referrer === '' && window.location.protocol === 'http:')
			);

			// Use redirect mode for embedded browsers to avoid COOP issues
			if (isEmbeddedBrowser && Platform.OS === 'web' && typeof window !== 'undefined') {
				// Build the authorization URL manually and redirect
				if (azureRequest && azureDiscovery?.authorizationEndpoint) {
					// Store state and code verifier in sessionStorage so we can retrieve them after redirect
					if (typeof window !== 'undefined' && window.sessionStorage) {
						sessionStorage.setItem('oauth_state', azureRequest.state || '');
						if (azureRequest.codeVerifier) {
							sessionStorage.setItem('oauth_code_verifier', azureRequest.codeVerifier);
						}
					}

					const authUrl = new URL(azureDiscovery.authorizationEndpoint);
					authUrl.searchParams.set('client_id', AZURE_CLIENT_ID);
					authUrl.searchParams.set('redirect_uri', redirectUri);
					authUrl.searchParams.set('response_type', 'code');
					authUrl.searchParams.set('scope', azureRequest.scopes?.join(' ') || '');
					authUrl.searchParams.set('state', azureRequest.state || '');
					if (azureRequest.codeChallenge) {
						authUrl.searchParams.set('code_challenge', azureRequest.codeChallenge);
						authUrl.searchParams.set('code_challenge_method', 'S256');
					}
					// Redirect to Microsoft login
					window.location.href = authUrl.toString();
					return; // Don't reset loading state, redirect will happen
				}
			}

			// Try popup mode first for regular browsers
			try {
				await promptAzureAsync();
			} catch (popupError: unknown) {
				// If popup fails due to COOP or other restrictions, fall back to redirect
				const error = popupError as { message?: string; code?: string };
				if (
					error?.message?.includes('Cross-Origin-Opener-Policy')
					|| error?.message?.includes('COOP')
					|| error?.message?.includes('popup')
					|| error?.code === 'ERR_CANCELED'
				) {
					if (Platform.OS === 'web' && typeof window !== 'undefined' && azureRequest && azureDiscovery?.authorizationEndpoint) {
						// Store state and code verifier in sessionStorage so we can retrieve them after redirect
						if (window.sessionStorage) {
							sessionStorage.setItem('oauth_state', azureRequest.state || '');
							if (azureRequest.codeVerifier) {
								sessionStorage.setItem('oauth_code_verifier', azureRequest.codeVerifier);
							}
						}

						const authUrl = new URL(azureDiscovery.authorizationEndpoint);
						authUrl.searchParams.set('client_id', AZURE_CLIENT_ID);
						authUrl.searchParams.set('redirect_uri', redirectUri);
						authUrl.searchParams.set('response_type', 'code');
						authUrl.searchParams.set('scope', azureRequest.scopes?.join(' ') || '');
						authUrl.searchParams.set('state', azureRequest.state || '');
						if (azureRequest.codeChallenge) {
							authUrl.searchParams.set('code_challenge', azureRequest.codeChallenge);
							authUrl.searchParams.set('code_challenge_method', 'S256');
						}
						// Redirect to Microsoft login
						window.location.href = authUrl.toString();
						return; // Don't reset loading state, redirect will happen
					}
				}
				// Re-throw if it's not a COOP/popup error
				throw popupError;
			}
		} catch (error) {
			console.error('Error prompting Azure sign-in:', error);
			setAzureLoading(false);
		}
	};

	const handleAppleSignIn = async () => {
		if (Platform.OS !== 'ios') return;

		setAppleLoading(true);
		try {
			// Check if Apple Authentication is available
			const isAvailable = await AppleAuthentication.isAvailableAsync();
			if (!isAvailable) {
				console.error('Apple Authentication is not available on this device');
				alert('Apple Sign In is not available on this device. Please use a different sign-in method.');
				return;
			}

			const credential = await AppleAuthentication.signInAsync({
				requestedScopes: [
					AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
					AppleAuthentication.AppleAuthenticationScope.EMAIL,
				],
			});

			if (!credential.identityToken) {
				console.error('No identity token received from Apple');
				alert('Sign in failed. No identity token received. Please try again.');
				return;
			}

			await authService.signInWithApple(credential.identityToken, {
				fullName: credential.fullName ? {
					givenName: credential.fullName.givenName || undefined,
					familyName: credential.fullName.familyName || undefined,
				} : undefined,
				email: credential.email || undefined,
			});

			// Add a small delay to ensure session change propagates
			await new Promise(resolve => setTimeout(resolve, 500));
			router.replace('/');

		} catch (error: unknown) {
			console.error('Apple Sign-In error:', error);

			const err = error as { code?: string; message?: string };
			if (err.code === 'ERR_REQUEST_CANCELED') {
				// User canceled the sign-in flow - no action needed
			} else if (err.code === 'ERR_REQUEST_NOT_HANDLED') {
				console.error('Apple Sign-In request not handled - this may indicate a configuration issue');
				alert('Sign in with Apple is not properly configured. Please contact support.');
			} else if (err.code === 'ERR_REQUEST_NOT_INTERACTIVE') {
				console.error('Apple Sign-In request not interactive - user may need to sign in through Settings');
				alert('Please sign in with Apple through your device Settings first, then try again.');
			} else if (err.message?.includes('authorization attempt failed')) {
				console.error('Apple authorization failed - configuration issue');
				alert('Sign in with Apple configuration error. Please contact support.');
			} else if (err.message?.includes('Unauthorized') || err.message?.includes('401')) {
				console.error('User not authorized in system');
				alert('Your account is not authorized to access this system. Please contact your administrator.');
			} else {
				console.error('Unexpected Apple Sign-In error:', error);
				alert('Sign in failed. Please try again or contact support if the problem persists.');
			}
		} finally {
			setAppleLoading(false);
		}
	};

	const handleDemoSignIn = async () => {
		try {
			setDemoLoading(true);
			await authService.enterDemoMode();
			await new Promise(resolve => setTimeout(resolve, 500));
			router.replace('/');
		} catch (error) {
			console.error('Demo mode entry failed:', error);
		} finally {
			setDemoLoading(false);
		}
	};

	return (
		<View style={styles.container}>
			{/* Azure/Microsoft sign in button */}
			{!azureDiscovery && (
				<Text style={styles.debugText}>Loading Microsoft sign-in...</Text>
			)}
			<TouchableOpacity
				accessibilityRole="button"
				accessibilityLabel="Sign in with Microsoft"
				testID="btn-signin-msft"
				style={styles.microsoftButton}
				disabled={!azureDiscovery || !azureRequest || azureLoading}
				onPress={handleAzureSignIn}>
				{azureLoading ? (
					<ActivityIndicator color="white" size="small" style={styles.buttonIcon} />
				) : (
					<Ionicons name="logo-microsoft" size={24} color="white" style={styles.buttonIcon} />
				)}
				<Text style={styles.buttonText}>Sign in with Microsoft</Text>
			</TouchableOpacity>
			{/* Apple Sign-In button (iOS only, when available) */}
			{Platform.OS === 'ios' && appleAvailable && (
				<TouchableOpacity
					accessibilityRole="button"
					accessibilityLabel="Sign in with Apple"
					testID="btn-signin-apple"
					style={styles.appleButton}
					disabled={appleLoading}
					onPress={handleAppleSignIn}>
					{appleLoading ? (
						<ActivityIndicator color="white" size="small" style={styles.buttonIcon} />
					) : (
						<Ionicons name="logo-apple" size={24} color="white" style={styles.buttonIcon} />
					)}
					<Text style={styles.buttonText}>Sign in with Apple</Text>
				</TouchableOpacity>
			)}

			{/* Try Demo — iOS-only; exists for App Review and screenshot automation */}
			{Platform.OS === 'ios' && (
				<TouchableOpacity
					accessibilityRole="button"
					accessibilityLabel="Try the demo"
					testID="btn-try-demo"
					style={styles.demoButton}
					disabled={demoLoading}
					onPress={handleDemoSignIn}>
					{demoLoading ? (
						<ActivityIndicator color="white" size="small" style={styles.buttonIcon} />
					) : (
						<Ionicons name="play-circle-outline" size={24} color="white" style={styles.buttonIcon} />
					)}
					<Text style={styles.buttonText}>Try the Demo</Text>
				</TouchableOpacity>
			)}

			<Text style={styles.versionText}>
				Version {version}
				{buildNumber && ` (build ${buildNumber})`}
			</Text>

			{/* API Debug Information */}
			<Text style={styles.debugText}>
				API: {apiUrl}
			</Text>
			<Text style={styles.debugText}>
				Status: {apiStatus}
			</Text>

			{/* Conditionally render AuthDebugButton */}
			{isMetaPressed && Platform.OS === 'web' && <AuthDebugButton />}
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: '#25292e',
		gap: 16,
	},
	googleButton: {
		backgroundColor: '#4285F4',
		flexDirection: 'row',
		paddingVertical: 12,
		paddingHorizontal: 32,
		borderRadius: 8,
		width: '80%',
		alignItems: 'center',
		justifyContent: 'center',
	},
	microsoftButton: {
		backgroundColor: '#2F2F2F',
		flexDirection: 'row',
		paddingVertical: 12,
		paddingHorizontal: 32,
		borderRadius: 8,
		width: '80%',
		alignItems: 'center',
		justifyContent: 'center',
	},
	demoButton: {
		backgroundColor: '#00897b',
		flexDirection: 'row',
		paddingVertical: 12,
		paddingHorizontal: 32,
		borderRadius: 8,
		width: '80%',
		alignItems: 'center',
		justifyContent: 'center',
	},

	buttonText: {
		color: 'white',
		fontSize: 16,
		fontWeight: 'bold',
	},
	buttonIcon: {
		marginRight: 8,
	},
	versionText: {
		color: '#aaa',
		fontSize: 12,
		marginTop: 16,
	},
	debugText: {
		color: '#888',
		fontSize: 10,
		marginTop: 2,
		textAlign: 'center',
	},
	smallText: {
		color: '#aaa',
		fontSize: 10,
		marginTop: 4,
	},
	noteText: {
		color: '#aaa',
		fontSize: 12,
		textAlign: 'center',
		marginTop: 8,
		fontStyle: 'italic',
	},
	appleButton: {
		backgroundColor: '#000',
		flexDirection: 'row',
		paddingVertical: 12,
		paddingHorizontal: 32,
		borderRadius: 8,
		width: '80%',
		alignItems: 'center',
		justifyContent: 'center',
	},
});

export default SignIn;
